/**
 * Scene.ts — high-level description of the *playing environment* the
 * simulator is currently running. A scene bundles together the things
 * the player picks before stepping on the boat:
 *
 *   - which boat they sail (`playerBoatId`)
 *   - which body of water / terrain preset (`terrainId`)
 *   - wind forecast (direction + speed at 10 m)
 *   - calendar date + local civil time (drives the sun position)
 *   - how crowded the marina is (0 = empty, 1 = packed)
 *
 * This module is *pure*. It does not import from `State.ts` directly so
 * it stays trivially unit-testable in Node. Bindings into the live
 * {@link AppState} go through {@link applySceneToState} which mutates
 * the fields the simulator already reads (wind, sun azimuth/elevation,
 * terrainId, playerBoatId). Sun azimuth + elevation are computed from
 * the scene's date/time + the terrain's geographic centre.
 *
 * Port congestion is exposed both as a normalised 0..1 fraction
 * (`SceneDefinition.portCongestion`) and as a derived integer count
 * via {@link congestionToMooredCount} once the caller knows the slip
 * capacity of the active terrain.
 *
 * Out of scope here: no fetching, no DOM, no rendering. Loading the
 * terrain's bbox to obtain lat/lon is the caller's job.
 */

import type { AppState } from "../State";

/** Geographic coordinates of the terrain centroid, in decimal degrees. */
export interface GeoCoord {
  /** Latitude, +N / -S, degrees. */
  latDeg: number;
  /** Longitude, +E / -W, degrees. */
  lonDeg: number;
}

/** Wind forecast at 10 m reference height. */
export interface SceneWind {
  /** Meteorological azimuth in degrees (0 = N, 90 = E, wind FROM that bearing). */
  directionDeg: number;
  /** Wind speed at 10 m, m/s. */
  speedMps: number;
}

/**
 * Local civil date + time. We keep it as an ISO 8601 string instead of a
 * `Date` so the scene survives JSON round-trips and so we don't accidentally
 * pick up the host's timezone. The optional `timezoneOffsetMinutes` lets us
 * compute solar time correctly without depending on `Intl`/`Temporal`.
 *
 * `timestampIso` is parsed as UTC. The offset describes how many minutes the
 * *local civil clock* is ahead of UTC (Polish summer time = +120, winter = +60).
 */
export interface SceneDateTime {
  /** ISO 8601 timestamp, e.g. `"2026-06-21T14:00:00Z"`. */
  timestampIso: string;
  /** Local-clock offset from UTC in minutes (e.g. +120 for CEST). */
  timezoneOffsetMinutes: number;
}

export interface SceneDefinition {
  playerBoatId: string;
  terrainId: string;
  wind: SceneWind;
  dateTime: SceneDateTime;
  /** Marina occupancy in [0..1]. 0 = no neighbour boats, 1 = every slip taken. */
  portCongestion: number;
}

/** Sensible default — Polish lake, mid-June afternoon, light breeze, quiet marina. */
export const DEFAULT_SCENE: SceneDefinition = {
  playerBoatId: "antila_27",
  terrainId: "zielona_zatoka",
  wind: { directionDeg: 270, speedMps: 6 },
  dateTime: {
    timestampIso: "2026-06-21T14:00:00Z",
    timezoneOffsetMinutes: 120, // CEST
  },
  portCongestion: 0,
};

/** Small library of named scene presets — useful for tests and quick-pick UIs later. */
export const SCENE_PRESETS: Record<string, SceneDefinition> = {
  calm_afternoon: DEFAULT_SCENE,
  fresh_breeze: {
    ...DEFAULT_SCENE,
    wind: { directionDeg: 225, speedMps: 11 },
    portCongestion: 0.4,
  },
  golden_hour: {
    ...DEFAULT_SCENE,
    dateTime: {
      timestampIso: "2026-06-21T18:30:00Z", // 20:30 CEST
      timezoneOffsetMinutes: 120,
    },
    wind: { directionDeg: 290, speedMps: 4 },
    portCongestion: 0.8,
  },
  morning_glass: {
    ...DEFAULT_SCENE,
    dateTime: {
      timestampIso: "2026-06-21T05:00:00Z", // 07:00 CEST
      timezoneOffsetMinutes: 120,
    },
    wind: { directionDeg: 0, speedMps: 1.5 },
    portCongestion: 0.2,
  },
};

// ---------------------------------------------------------------------------
// Sun position
// ---------------------------------------------------------------------------

/** Solar position output, both angles in degrees, conventional astro frame. */
export interface SunPosition {
  /** 0 = north, 90 = east, 180 = south, 270 = west. */
  azimuthDeg: number;
  /** Angle above horizon. Negative means the sun is below the horizon. */
  elevationDeg: number;
}

const DEG = Math.PI / 180;
const RAD = 180 / Math.PI;

/**
 * Day-of-year (1..365/366) for the given UTC date.
 */
function dayOfYearUtc(date: Date): number {
  const start = Date.UTC(date.getUTCFullYear(), 0, 0);
  const diff = date.getTime() - start;
  return Math.floor(diff / 86_400_000);
}

/**
 * Compute the sun's azimuth + elevation for a given UTC instant and observer
 * location. Implementation follows the standard NOAA / Spencer simplification
 * which is accurate to ~0.5° — more than enough for sky shading.
 *
 * @param date UTC timestamp.
 * @param latDeg Observer latitude in degrees (+N, -S).
 * @param lonDeg Observer longitude in degrees (+E, -W).
 */
export function computeSunPosition(
  date: Date,
  latDeg: number,
  lonDeg: number,
): SunPosition {
  const N = dayOfYearUtc(date);
  // Fractional year γ (radians) — Spencer 1971.
  const gamma = (2 * Math.PI * (N - 1 + (date.getUTCHours() - 12) / 24)) / 365;

  // Equation of time in minutes.
  const eqTime =
    229.18 *
    (0.000075 +
      0.001868 * Math.cos(gamma) -
      0.032077 * Math.sin(gamma) -
      0.014615 * Math.cos(2 * gamma) -
      0.040849 * Math.sin(2 * gamma));

  // Solar declination in radians.
  const decl =
    0.006918 -
    0.399912 * Math.cos(gamma) +
    0.070257 * Math.sin(gamma) -
    0.006758 * Math.cos(2 * gamma) +
    0.000907 * Math.sin(2 * gamma) -
    0.002697 * Math.cos(3 * gamma) +
    0.00148 * Math.sin(3 * gamma);

  // True solar time in minutes since local solar midnight.
  const utcMinutes =
    date.getUTCHours() * 60 + date.getUTCMinutes() + date.getUTCSeconds() / 60;
  const trueSolarTime = (utcMinutes + eqTime + 4 * lonDeg) % 1440;

  // Hour angle in degrees, then radians. Noon = 0, afternoon positive.
  let haDeg = trueSolarTime / 4 - 180;
  if (haDeg < -180) haDeg += 360;
  const ha = haDeg * DEG;
  const lat = latDeg * DEG;

  const sinElev =
    Math.sin(lat) * Math.sin(decl) +
    Math.cos(lat) * Math.cos(decl) * Math.cos(ha);
  const elev = Math.asin(Math.max(-1, Math.min(1, sinElev)));

  // Azimuth from north, clockwise. Uses atan2 form to keep the branch right.
  const sinAz = -Math.cos(decl) * Math.sin(ha);
  const cosAz =
    Math.sin(decl) * Math.cos(lat) -
    Math.cos(decl) * Math.sin(lat) * Math.cos(ha);
  let azDeg = Math.atan2(sinAz, cosAz) * RAD;
  if (azDeg < 0) azDeg += 360;

  return { azimuthDeg: azDeg, elevationDeg: elev * RAD };
}

/**
 * Helper: read the scene's local civil clock as a UTC `Date`. The stored
 * timestamp is treated as already-UTC; the local offset is informational
 * (kept for round-tripping into a UI later).
 */
export function sceneInstantToUtc(dt: SceneDateTime): Date {
  return new Date(dt.timestampIso);
}

// ---------------------------------------------------------------------------
// Port congestion
// ---------------------------------------------------------------------------

/**
 * Map a normalised congestion fraction to an integer neighbour-boat count.
 *
 * The mapping uses `Math.round` so 0 stays empty and 1 fills capacity.
 * Values outside [0..1] are clamped.
 */
export function congestionToMooredCount(
  congestion: number,
  capacity: number,
): number {
  if (capacity <= 0) return 0;
  const c = Math.max(0, Math.min(1, congestion));
  return Math.round(c * capacity);
}

// ---------------------------------------------------------------------------
// Scene <-> AppState mapping
// ---------------------------------------------------------------------------

/** Context the scene needs in order to fully resolve into runtime state. */
export interface SceneApplyContext {
  /**
   * Geographic centre of the active terrain. Required for sun position;
   * if omitted, sun fields in {@link AppState} are left unchanged.
   */
  terrainCenter?: GeoCoord;
}

/**
 * Project a scene onto a mutable {@link AppState}, overwriting the fields
 * the scene owns. Fields not covered by a scene (mooring assignments, water
 * level fine-tune, render toggles) are left untouched so existing gameplay
 * state survives a scene swap.
 *
 * Returns the same `state` reference for convenience.
 */
export function applySceneToState(
  scene: SceneDefinition,
  state: AppState,
  ctx: SceneApplyContext = {},
): AppState {
  state.playerBoatId = scene.playerBoatId;
  state.terrainId = scene.terrainId;
  state.windDirectionDeg = scene.wind.directionDeg;
  state.windSpeed = scene.wind.speedMps;

  if (ctx.terrainCenter) {
    const sun = computeSunPosition(
      sceneInstantToUtc(scene.dateTime),
      ctx.terrainCenter.latDeg,
      ctx.terrainCenter.lonDeg,
    );
    state.sunAzimuthDeg = sun.azimuthDeg;
    // Clamp so we don't feed a below-horizon sun to the shading uniforms
    // until the renderer grows proper night handling. Tiny positive floor
    // keeps the directional light still defined.
    state.sunElevationDeg = Math.max(2, sun.elevationDeg);
  }

  return state;
}

/**
 * Inverse of {@link applySceneToState} — synthesise a scene from a live
 * AppState. The date/time round-trip is *not* possible (state only stores
 * the resulting sun angles), so the caller supplies the timestamp they want
 * to attach.
 */
export function sceneFromAppState(
  state: AppState,
  dateTime: SceneDateTime,
  portCongestion = 0,
): SceneDefinition {
  return {
    playerBoatId: state.playerBoatId,
    terrainId: state.terrainId,
    wind: {
      directionDeg: state.windDirectionDeg,
      speedMps: state.windSpeed,
    },
    dateTime,
    portCongestion,
  };
}

/**
 * Read the geographic centre of a parsed `terrain_meta.json` bbox.
 * Pure helper — callers fetch the JSON themselves.
 */
export function terrainCenterFromBbox(bbox: {
  lon_min: number;
  lat_min: number;
  lon_max: number;
  lat_max: number;
}): GeoCoord {
  return {
    latDeg: 0.5 * (bbox.lat_min + bbox.lat_max),
    lonDeg: 0.5 * (bbox.lon_min + bbox.lon_max),
  };
}
